/**
 * ★ THE REAL ADAPTER - the `AuthClient` port (./auth-port.ts) over the Firebase web SDK.
 *
 * CHOSEN IN ONE PLACE. ./create-auth-client.ts builds it when `VITE_AUTHORS_AUTH=firebase` and at
 * no other time, so the default build keeps the fixture and never calls `initializeApp`. Under
 * `dev:stack` (web/authors/package.json) it signs in against the dev stack's Auth emulator,
 * 127.0.0.1:9099 (infra/compose.dev.yml), project `demo-moonx-authors`
 * (infra/compose.dev.yml); with no emulator URL it is the real Firebase Auth service.
 *
 * ONLY `firebase/app` AND `firebase/auth`, so no other Firebase product enters the bundle. The
 * import is STATIC ON PURPOSE: a popup has to open inside the click that asked for it, and an
 * `await import()` in front of `signInWithPopup` would spend that click on a network fetch. The
 * SDK works to the same budget - it pre-loads its popup machinery on Safari and mobile, because
 * "Mobile browsers and Safari need to optimistically initialize" (SDK:10833-10836).
 *
 * `SDK:NNN` IS A LINE OF THE INSTALLED SDK, READ RATHER THAN RECALLED: `@firebase/auth` 1.13.6,
 * which `firebase` 12.19.0 installs, in its browser build dist/esm/index-4NFEPWkC.js (the package
 * maps the `browser` import condition to dist/esm/index.js, which re-exports that file).
 *
 * ── WHAT THE SDK DOES, AND WHAT THAT MAKES THIS FILE DO ────────────────────────────────────────
 *
 * ONE INSTANCE PER PAGE. `getAuth` is the browser default - IndexedDB, then localStorage, then
 * sessionStorage, plus the popup resolver (SDK:11262-11274) - and it is called once, below, on top
 * of the memoised `authClient()`: an auth client with two instances has two observer sets.
 *
 * THE EMULATOR IS CONNECTED FIRST. The SDK refuses a first `connectAuthEmulator` once it has sent a
 * request (SDK:3883-3894; every request clears the flag, SDK:933), and restoring a saved session
 * sends one (SDK:2843, :2874-2876). `disableWarnings` is not cosmetic: without it the SDK pins a
 * fixed banner in raw colours over the page (SDK:3937-3968). Against the emulator the
 * authorized-domain check is skipped (SDK:10311-10315), so the laptop origin needs no console
 * entry.
 *
 * ALL THREE CONFIG VALUES ARE REQUIRED, EVEN AGAINST THE EMULATOR. The popup's iframe URL asserts
 * `authDomain` (SDK:10507-10512), so a build without one would fail at the first click instead of
 * at start. A missing value refuses to start and says which one.
 *
 * `subscribe` IS `onIdTokenChanged`, DE-DUPLICATED - NOT `onAuthStateChanged`, BECAUSE OF G30.
 * Both fire once after persistence is restored, null included and asynchronously (SDK:3115-3135),
 * which is what ends the provider's `booting` phase (../session/session-state.ts). But the
 * auth-state observer fires only when the UID changes (SDK:3104-3114), and a link KEEPS the uid:
 * `linkWithPopup` refreshes the tokens and reloads the account - which is where `isAnonymous` turns
 * false (SDK:1512-1514) - and then notifies the id-token observers alone (SDK:5872-5875,
 * :5689-5698, :1828-1842). Over `onAuthStateChanged` the cabinet would go on treating a linked
 * reader as a guest. The id-token observer also fires on every token refresh (SDK:1763-1771), so
 * each subscription forwards only a CHANGE OF IDENTITY - the port's plain data - plus its first
 * answer, which is never swallowed. The `User` behind a session survives a same-uid update, even
 * one from another tab (SDK:2752-2759), so a session handed out earlier stays live.
 *
 * `isAnonymous` IS THE SDK'S OWN FLAG. Only `signInAnonymously` sets it (SDK:5757); a custom-token
 * sign-in - the hand-off - never does (SDK:5678, :6070), whichever account the token names. G30 is
 * judged on the token's sign-in provider at submit time (docs/design/03-worksheet.md), and a
 * `custom` token is not an `anonymous` one.
 *
 * ERRORS go through the seam's ONE table (`MAPPED_SDK_ERRORS`, verified line by line there), handed
 * in as `mapSdkError` so that this file and the seam do not import each other. Only the SDK's own
 * `auth/` codes are mapped; any other failure is rethrown as it came, never swallowed.
 */
import { initializeApp } from 'firebase/app';
import type { FirebaseApp } from 'firebase/app';
import * as sdk from 'firebase/auth';
import type { ActionCodeSettings, Auth, AuthProvider, User } from 'firebase/auth';
import { returnPathFrom, signInPathFor } from '../navigation';
import type { AuthClient, AuthIdentity, AuthSession, PopupProvider } from './auth-port';

/** The seam's `toAuthPortError`: an SDK error code, and the error itself as the cause. */
export type SdkErrorMapper = (sdkErrorCode: string, cause?: unknown) => Error;

/** The build-time web config. None of it is secret - it ships in every browser bundle. */
interface FirebaseWebConfig {
  readonly apiKey: string;
  readonly authDomain: string;
  readonly projectId: string;
  /** Set only against the local stack (src/vite-env.d.ts). */
  readonly emulatorUrl: string | null;
}

function present(value: string | undefined): value is string {
  return typeof value === 'string' && value.length > 0;
}

function readFirebaseConfig(): FirebaseWebConfig {
  // Read at CALL time, one dotted name at a time, which is the form Vite inlines at build.
  const apiKey = import.meta.env.VITE_FIREBASE_API_KEY;
  const authDomain = import.meta.env.VITE_FIREBASE_AUTH_DOMAIN;
  const projectId = import.meta.env.VITE_FIREBASE_PROJECT_ID;
  const emulatorUrl = import.meta.env.VITE_FIREBASE_AUTH_EMULATOR_URL;

  if (!present(apiKey) || !present(authDomain) || !present(projectId)) {
    const missing = [
      present(apiKey) ? null : 'VITE_FIREBASE_API_KEY',
      present(authDomain) ? null : 'VITE_FIREBASE_AUTH_DOMAIN',
      present(projectId) ? null : 'VITE_FIREBASE_PROJECT_ID',
    ].filter((name): name is string => name !== null);
    throw new Error(
      `VITE_AUTHORS_AUTH=firebase was set, but the build has no value for ${missing.join(', ')}. ` +
        'The Firebase web config is build-time env: web/authors/.env.example says what each ' +
        'value is, and the dev:stack script in web/authors/package.json sets all of them.',
    );
  }
  // ★ `self` (the tunnel mode, `dev:tunnel`): the emulator is reached through this page's own origin,
  // which the dev server forwards to it (`vite.config.ts`) - a teammate's `127.0.0.1` is their own machine.
  const emulator = present(emulatorUrl)
    ? emulatorUrl === 'self'
      ? window.location.origin
      : emulatorUrl
    : null;
  return { apiKey, authDomain, projectId, emulatorUrl: emulator };
}

/** The one SDK instance for this page - see ONE INSTANCE PER PAGE above. */
let sdkAuth: Auth | null = null;

function firebaseAuth(): Auth {
  if (sdkAuth !== null) {
    return sdkAuth;
  }
  const config = readFirebaseConfig();
  const auth = sdk.getAuth(
    initializeApp({
      apiKey: config.apiKey,
      authDomain: config.authDomain,
      projectId: config.projectId,
    }),
  );
  // Before anything else touches `auth` - see THE EMULATOR IS CONNECTED FIRST above.
  if (config.emulatorUrl !== null) {
    sdk.connectAuthEmulator(auth, config.emulatorUrl, { disableWarnings: true });
  }
  sdkAuth = auth;
  return auth;
}

/**
 * The page's one Firebase app - the one `firebaseAuth()` initialised - for the one other product
 * the cabinet uses on it: Storage, in ../../storage/firebase-quarantine-uploader.ts.
 *
 * IT MUST BE THIS APP. Storage sends the ID token of the Auth instance registered on its own app,
 * and no token when there is none, and the quarantine rule checks that token's uid against the
 * path being written (adr/0010-firebase-integration.md) - so a Storage instance on any other
 * app writes as nobody. Asked THROUGH `firebaseAuth()` rather than kept in a second variable, so
 * Auth is on the app, its emulator connected, before Storage can exist; and the SDK is initialised
 * here exactly as `createFirebaseAuthClient` would initialise it, once per page.
 */
export function firebaseApp(): FirebaseApp {
  return firebaseAuth().app;
}

/**
 * What each provider id is called on screen - and the words the payout re-sign-in reads to pick
 * its popup (./auth-port.ts), so an id missing here is shown as itself. A magic-link account's is
 * `password`: the e-mail link is a sign-in method of the password provider (SDK:4746, :4754,
 * :4163), and this platform offers no passwords, so here it can only mean the link.
 */
const PROVIDER_LABELS: Readonly<Record<string, string>> = {
  'apple.com': 'Apple',
  'google.com': 'Google',
  password: 'E-mail link',
};

/** PLAIN DATA, copied field by field, so no live SDK object reaches the reducer (./auth-port.ts) */
function identityOf(user: User): AuthIdentity {
  return {
    uid: user.uid,
    isAnonymous: user.isAnonymous,
    providerLabels: user.providerData.map(
      (info) => PROVIDER_LABELS[info.providerId] ?? info.providerId,
    ),
    displayName: user.displayName,
    email: user.email,
  };
}

/**
 * The session holds the LIVE `User`, which is the only thing that can mint a token. The SDK caches
 * the ID token and refreshes it near expiry, so asking on every call costs nothing; `forceRefresh`
 * reaches the SDK only when the caller says so - the one forced refresh a bare 401 is worth.
 */
function sessionOf(user: User): AuthSession {
  return {
    identity: identityOf(user),
    getIdToken: (forceRefresh?: boolean) => user.getIdToken(forceRefresh === true),
  };
}

function sameIdentity(a: AuthIdentity | null, b: AuthIdentity | null): boolean {
  if (a === null || b === null) {
    return a === b;
  }
  return (
    a.uid === b.uid &&
    a.isAnonymous === b.isAnonymous &&
    a.displayName === b.displayName &&
    a.email === b.email &&
    a.providerLabels.length === b.providerLabels.length &&
    a.providerLabels.every((label, index) => label === b.providerLabels[index])
  );
}

/** A fresh provider per popup; Apple has no class of its own in the SDK and is named by id. */
function providerFor(provider: PopupProvider): AuthProvider {
  switch (provider) {
    case 'google':
      return new sdk.GoogleAuthProvider();
    case 'apple':
      return new sdk.OAuthProvider('apple.com');
  }
}

/**
 * The SDK's code, when the failure is the SDK's own: a `FirebaseError` whose `code` is `auth/` plus
 * the SDK's internal code (see `MAPPED_SDK_ERRORS` in the seam for where that prefix comes from).
 */
function sdkErrorCode(error: unknown): string | null {
  if (typeof error !== 'object' || error === null || !('code' in error)) {
    return null;
  }
  return typeof error.code === 'string' && error.code.startsWith('auth/') ? error.code : null;
}

function portFailure(error: unknown, mapSdkError: SdkErrorMapper): Error {
  const code = sdkErrorCode(error);
  if (code !== null) {
    return mapSdkError(code, error);
  }
  return error instanceof Error ? error : new Error(String(error));
}

/** The link lands on `/sign-in` (its handler's route) carrying this page's own safe `next`. */
function magicLinkSettings(): ActionCodeSettings {
  // `handleCodeInApp` is required for an e-mail link (SDK:6486); the `next` brings the author back.
  const back = signInPathFor(returnPathFrom(window.location.search));
  return { url: `${window.location.origin}${back}`, handleCodeInApp: true };
}

/**
 * Builds the adapter over the page's one SDK instance, initialising it on the first call. Throws,
 * naming the variable, when the build carries no Firebase config.
 *
 * NOTHING IS AWAITED BEFORE A POPUP OPENS: each popup method calls the SDK first, so the window
 * still opens inside the click that asked for it.
 */
export function createFirebaseAuthClient(mapSdkError: SdkErrorMapper): AuthClient {
  const auth = firebaseAuth();
  const fail = (error: unknown): Error => portFailure(error, mapSdkError);

  return {
    currentSession: () => {
      const user = auth.currentUser;
      return user === null ? null : sessionOf(user);
    },

    subscribe: (listener) => {
      // `undefined` means nothing has been reported yet, so the FIRST answer - null included -
      // always goes through. That answer is the one that ends `booting`.
      let reported: AuthIdentity | null | undefined;
      return sdk.onIdTokenChanged(auth, (user) => {
        const session = user === null ? null : sessionOf(user);
        const identity = session === null ? null : session.identity;
        if (reported !== undefined && sameIdentity(reported, identity)) {
          // A token refresh that changed nothing about the person. Not a change.
          return;
        }
        reported = identity;
        listener(session);
      });
    },

    signInWithPopup: async (provider) => {
      try {
        const credential = await sdk.signInWithPopup(auth, providerFor(provider));
        return sessionOf(credential.user);
      } catch (error) {
        throw fail(error);
      }
    },

    signInWithCustomToken: async (customToken) => {
      try {
        const credential = await sdk.signInWithCustomToken(auth, customToken);
        return sessionOf(credential.user);
      } catch (error) {
        throw fail(error);
      }
    },

    // G30's remedy: onto the CURRENT user, whose uid a link keeps. ../session/link-anonymous.ts
    // compares the uid this returns with the one it had and refuses a result that moved it.
    linkWithPopup: async (provider) => {
      const user = auth.currentUser;
      if (user === null) {
        throw new Error('There is no signed-in account to link a provider to.');
      }
      try {
        const credential = await sdk.linkWithPopup(user, providerFor(provider));
        return sessionOf(credential.user);
      } catch (error) {
        throw fail(error);
      }
    },

    sendMagicLink: async (email) => {
      try {
        await sdk.sendSignInLinkToEmail(auth, email, magicLinkSettings());
      } catch (error) {
        throw fail(error);
      }
    },

    // A parse of the URL and nothing else - no request (SDK:6502-6505, :4623-4654).
    isMagicLinkUrl: (url) => sdk.isSignInWithEmailLink(auth, url),

    completeMagicLink: async (url, email) => {
      try {
        // The address first and the link second: that is the SDK's order (SDK:6547).
        const credential = await sdk.signInWithEmailLink(auth, email, url);
        return sessionOf(credential.user);
      } catch (error) {
        throw fail(error);
      }
    },

    // Local, like the port says. Not mapped: the table is about signing in.
    signOut: () => sdk.signOut(auth),
  };
}
